import { Dialog } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Modal } from '@/components/common/modal';
import { Button } from '@/components/form/button';
import { Input } from '@/components/form/input';

import { requestPasswordReset } from '../services/auth';
import { AuthQueryKeys } from '../services/query-keys';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close */
  toggle: () => void;
}

export type PasswordForgotRequestFormFields = {
  email: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email('commonValidation.emailNotValid')
    .required('commonValidation.fieldRequired'),
});

export const ForgotPasswordModal: React.FC<IProps> = ({ isOpen, toggle }) => {
  const { t } = useTranslation('translation', { useSuspense: false });
  const [email, setEmail] = useState('');
  const [isQueryEnabled, setIsQueryEnabled] = useState<boolean>(false);

  const {
    reset,
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordForgotRequestFormFields>({
    resolver: yupResolver(validationSchema),
  });

  const { isFetching } = useQuery({
    retry: false,
    queryKey: [AuthQueryKeys.RequestPasswordReset, email],
    queryFn: ({ queryKey }) => email && requestPasswordReset({ email: queryKey[1]! }),
    enabled: isQueryEnabled,
    onSuccess: () => {
      handleOnClose();
      toast.success(t('forgotPassword.successResetRequest'), { duration: 6000 });
    },
    onError: () => {
      setError('email', { message: 'forgotPassword.errorMessage' });
    },
    onSettled: () => {
      setIsQueryEnabled(false);
    },
  });

  const onSubmit = handleSubmit((data) => {
    setIsQueryEnabled(true);
    setEmail(data.email);
  });

  const handleOnClose = () => {
    reset();
    setEmail('');
    setIsQueryEnabled(false);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleOnClose} title={t('forgotPassword.title')}>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-3 py-6">
          <Dialog.Description>{t('forgotPassword.description')}</Dialog.Description>
          <Input<PasswordForgotRequestFormFields>
            type="text"
            name="email"
            variant="solid"
            className="w-72"
            autoComplete="username"
            placeholder={t('login.email')}
            register={register}
            errors={errors}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" isLoading={isFetching}>
            {t('forgotPassword.request')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
